import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import {
  freshDraft,
  holdWhileComposing,
  writeDraft,
  forgetDraft,
  useDraft,
  type Draft,
} from "./formDraft";
import {
  preview,
  newPlacement,
  project,
  type Edits,
  type Edit,
} from "../funnel/formEditor";
import { useForms } from "../funnel/formQueries";
import { settingId } from "../funnel/useForm";
import {
  DECORATIVE,
  WIDTHS,
  fetchQuestions,
  questionsFor,
  createQuestion,
  type Field,
  type Form,
  type Question,
  type RawModel,
  type Width,
} from "../funnel/forms";
import { useSettings } from "../settings/context";
import { Button } from "../ui/Button";
import { NewQuestion } from "./NouvelleDonnee";


/**
 * ═══ LES CARTOUCHES « CONDITION » DÉPLIÉS, hors du composant ═══
 *
 * ⚠️ UN `useState` ICI SE PERDAIT À CHAQUE ENREGISTREMENT AUTOMATIQUE (vécu le
 * 2026-09-13) : l'envoi invalide la requête des formulaires, `form` manque le temps de la
 * relire, l'éditeur rend `null`, et au remontage l'état local est neuf — le cartouche
 * qu'on était en train de régler se refermait sous la souris toutes les six secondes.
 * Même cause, même remède que le brouillon (`formDraft.ts`) : l'état vit au niveau du
 * module et survit au démontage. Par formulaire, parce que deux éditeurs peuvent être
 * ouverts (participant et société).
 */
const unfoldedByForm = new Map<number, Set<number>>();
const unfoldedWatchers = new Set<() => void>();
const NONE: ReadonlySet<number> = new Set();

function useUnfolded(formId: number | undefined): ReadonlySet<number> {
  const read = useCallback(
    () => (formId === undefined ? NONE : (unfoldedByForm.get(formId) ?? NONE)),
    [formId],
  );
  return useSyncExternalStore(
    (notify) => {
      unfoldedWatchers.add(notify);
      return () => {
        unfoldedWatchers.delete(notify);
      };
    },
    read,
    read,
  );
}

/**
 * ⚠️ PAR QUESTION, PAS PAR PLACEMENT. La route d'enregistrement supprime puis réinsère
 * les placements : leurs identifiants changent à chaque envoi, et un cartouche déplié
 * pour l'ancien identifiant ne reconnaîtrait plus son champ. La question (`modelId`)
 * reste la même ; seul un décorateur n'en a pas, et il n'a pas de condition.
 */
const stableKey = (f: Field) => f.modelId ?? Number(f.id);

function writeUnfolded(formId: number, next: Set<number>) {
  unfoldedByForm.set(formId, next);
  unfoldedWatchers.forEach((notify) => notify());
}

/**
 * LE FORMULAIRE DE L'ÉTAPE INFORMATIONS — sa PRÉSENTATION, réglée ici.
 *
 * ⚠️ CE N'EST PAS UN CONSTRUCTEUR DE CHAMPS, ET LA NUANCE EST TOUTE L'AFFAIRE. Le
 * dépôt interdit trois fois de rebâtir le formulaire dans la V2, et il a raison : ce
 * serait une SECONDE DÉFINITION de chaque champ, libre de diverger de celle sur
 * laquelle les badges sont imprimés. Cet écran n'en crée aucune. Il écrit dans les
 * MÊMES lignes `FormModels` que le back office — une définition, deux fenêtres.
 *
 * La QUESTION elle-même (sa clé, son libellé, ses options, ses quotas) vit dans
 * `Translations`, partagée par tous les formulaires du domaine, et reste au back
 * office. D'où un libellé en lecture seule ici : le changer changerait le formulaire
 * du voisin.
 *
 * ⚠️ ENREGISTRER RÉÉCRIT LE FORMULAIRE ENTIER, parce que la route détruit puis
 * réinsère. Tout ce qui n'est pas renvoyé disparaît — voir `funnel/formEditor.ts`, qui
 * fait suivre les lignes brutes pour cette raison exacte.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/**
 * ⚠️ « LISTE (PLUSIEURS) » N'EST PAS DANS LA LISTE, ET C'EST DÉLIBÉRÉ. Le composant
 * `select-multiple` existe bien côté produit, mais la V2 le rend EXACTEMENT comme
 * `checkbox` : `MULTIPLE` regroupe les deux et une seule branche les dessine. L'offrir
 * revenait donc à proposer deux entrées pour un même résultat — un choix sans effet,
 * que l'organisateur ne pouvait comprendre qu'en essayant.
 *
 * Le rendre différemment a été essayé : un `<select multiple>` natif. Il a été retiré
 * parce que sur ordinateur un clic simple REMPLACE la sélection au lieu de l'étendre,
 * et que quelqu'un qui l'ignore croit avoir retenu trois entrées et n'en envoie qu'une
 * — une perte de réponse silencieuse, au milieu d'une inscription. Les cases, elles,
 * ne mentent jamais sur ce qui est coché.
 *
 * Un champ réglé en `select-multiple` au back office reste donc servi en cases. Il ne
 * disparaît pas de l'éditeur pour autant : voir `typeDe`.
 */
const COMPONENTS: Array<{ value: Field["component"]; label: string }> = [
  { value: "input", label: "Texte" },
  { value: "input-number", label: "Nombre" },
  { value: "textarea", label: "Plusieurs lignes" },
  { value: "phone-number", label: "Téléphone" },
  { value: "select", label: "Liste (un choix)" },
  { value: "radio", label: "Boutons (un choix)" },
  { value: "checkbox", label: "Cases (plusieurs)" },
  { value: "file", label: "Pièce jointe" },
];

/**
 * POUR UNE PIÈCE JOINTE : ce qu'on accepte, en familles (la syntaxe est celle de
 * l'attribut `accept`, la même que le back office écrit), et le plafond en Mo.
 * `""` = tout. Une valeur réglée ailleurs qui n'est dans aucune famille s'affiche
 * telle quelle en fin de liste, pour la même raison que `typeDe`.
 */
const ACCEPTS: Array<{ value: string; label: string }> = [
  { value: "", label: "Tout fichier" },
  { value: ".pdf", label: "PDF" },
  { value: "image/*", label: "Images" },
  { value: ".pdf,image/*", label: "PDF ou images" },
  { value: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx", label: "Documents (PDF, Office)" },
];
const MAX_MBS = [1, 2, 5, 10, 20, 50];
function acceptsOf(value: string) {
  return ACCEPTS.some((a) => a.value === value)
    ? ACCEPTS
    : [...ACCEPTS, { value, label: `${value} — réglé au back office` }];
}

/** Les types qui affichent les choix de la question. */
const WITH_CHOICES = new Set<Field["component"]>([
  "select",
  "select-multiple",
  "radio",
  "checkbox",
]);

/**
 * LA LISTE DES TYPES POUR *CE* CHAMP — celle du dessus, réduite à ce qui lui convient,
 * plus le sien s'il n'y figure pas.
 *
 * ⚠️ UNE QUESTION À CHOIX NE SE PROPOSE QU'EN TYPE À CHOIX, ET L'INVERSE (#287). C'est
 * la règle du back office (`componentsMultiple` dès que la question a des valeurs,
 * `components` sinon) : en « Texte », une question à choix cachait ses choix au
 * participant et enregistrait une chaîne au lieu de `{clé: true}` ; en « Liste », une
 * question sans choix ne laissait rien à choisir.
 *
 * ⚠️ SANS ÇA, LE MENU S'AFFICHE VIDE. Un `<select>` dont la valeur ne correspond à
 * aucune `<option>` ne montre rien : un champ réglé en `select-multiple` au back
 * office — ou dans un composant qu'une version future ajouterait — se serait présenté
 * avec un type blanc, comme s'il n'en avait pas. On l'ajoute donc en fin de liste,
 * nommé, pour que l'organisateur LISE ce qui est réglé même quand ce n'est plus
 * proposé. Le choisir à nouveau reste impossible : une fois changé, il sort de la
 * liste.
 */
function typeDe(component: Field["component"], hasChoices: boolean) {
  const offered = COMPONENTS.filter(
    (c) => WITH_CHOICES.has(c.value) === hasChoices,
  );
  if (offered.some((c) => c.value === component)) return offered;
  /* Un type connu mais qui ne convient pas (une question à choix déjà enregistrée en
     « Texte ») reste lisible, nommé comme tel : une fois changé, il sort de la liste. */
  const known = COMPONENTS.find((c) => c.value === component);
  const name =
    component === "select-multiple"
      ? "Liste (plusieurs), servie en cases"
      : known
        ? `${known.label} — ne convient pas à cette question`
        : `${component} — non modifiable ici`;
  return [...offered, { value: component, label: name }];
}

/** La valeur sentinelle de la liste d'ajout : « créer » plutôt que « poser ». */
const CREATE = "__creer";

/** Les composants qui dessinent des cases ou des boutons — ceux qu'on peut ranger. */
const WITH_BOXES = new Set(["radio", "checkbox", "select-multiple"]);

/* ⚠️ DES CONSTANTES, PAS DES LITTÉRAUX. Un `new Map()` écrit dans le corps du composant
   change de référence à chaque rendu : les `useMemo` qui en dépendent se recalculent
   toujours, et l'effet qui pousse l'aperçu réécrit le cache en boucle. */
const NO_PATCH: Map<number, Edit> = new Map();
const NO_ID: ReadonlySet<number> = new Set();
const NO_PLACEMENT: RawModel[] = [];

/* Des intitulés COURTS : cette liste dispose d'à peine cent pixels dans un panneau de
   380. « Pleine largeur » y était tronqué, et le mot ne se devine pas à la moitié —
   alors qu'au milieu de Petit / Moyen / Grand, « Pleine » ne demande aucun effort. Le
   sens complet est dans l'aide de la ligne, et `aria-label` dit « largeur » au lecteur
   d'écran. */
const WIDTH_NAMES: Record<Width, string> = {
  petit: "Petit",
  moyen: "Moyen",
  grand: "Grand",
  pleine: "Pleine",
};

export function FormEditor({
  /**
   * Le réglage qui NOMME le formulaire à ouvrir. Deux étapes en servent un — le
   * participant et la société — et cette clé était écrite en dur ici : la seconde
   * aurait édité le formulaire de la première, dont l'enregistrement réécrit toutes
   * les lignes. Déclarée sur la ligne du catalogue, jamais devinée.
   */
  formKey,
}: {
  formKey: string;
}) {
  const event = useEvent();
  const { value, saveMode } = useSettings();
  const client = useQueryClient();


  /* La lecture partagée : le formulaire est déjà en cache quand on ouvre le panneau,
     et l'enregistrement l'invalide pour tout le monde — voir `funnel/formQueries.ts`. */
  const forms = useForms();

  /** Les questions du domaine — ce qu'on peut poser dans ce formulaire. */
  const { data: questions } = useQuery({
    queryKey: ["questions", event?.domainId, event?.eventId],
    queryFn: ({ signal }) =>
      fetchQuestions(event!.domainId, event!.eventId, signal),
    enabled: !!event,
    staleTime: Infinity,
    retry: false,
  });

  /**
   * ⚠️ « AUCUN CHOIX » ET « CE CHOIX-CI » RESTENT DEUX CHOSES, et c'est `settingId` qui
   * les sépare partout ailleurs : le panneau écrit la CHAÎNE VIDE quand on remet un
   * sélecteur sur « — » (`Row.tsx`), que la comparaison brute d'avant traitait comme un
   * identifiant introuvable — indiscernable, ici, d'un formulaire encore en vol.
   */
  const chosen = settingId(value(formKey));
  const duCache = forms.find((f) => String(f.id) === chosen);

  /**
   * ⚠️ LE BROUILLON SURVIT AU DÉMONTAGE, ET C'EST TOUT L'INTÉRÊT. Cet éditeur
   * s'affiche aussi dans la carte du `SpotEditor`, qui se referme dès que la souris la
   * quitte : gardées dans un `useState`, les retouches disparaissaient au premier geste
   * de travers. Elles vivent donc dans `formDraft`, hors du composant.
   *
   * ⚠️ ET LA VÉRITÉ DU SERVEUR VIENT DU BROUILLON, PAS DU CACHE. Dès qu'un brouillon
   * existe, le cache contient l'APERÇU — le relire ici capturerait les retouches comme
   * si elles venaient du serveur : elles s'appliqueraient une seconde fois, et la garde
   * anti-écrasement comparerait le formulaire à lui-même retouché.
   */
  const draft = useDraft(duCache?.id);
  const form = draft?.base ?? duCache;

  const edits: Edits = useMemo(
    () => ({
      patches: draft?.patches ?? NO_PATCH,
      order: draft?.order ?? (form?.fields ?? []).map((f) => Number(f.id)),
      removed: draft?.removed ?? NO_ID,
      additions: draft?.additions ?? NO_PLACEMENT,
    }),
    [draft, form],
  );

  /* La liste de l'éditeur EST l'aperçu : une seule projection, donc aucun risque que
     le panneau montre une chose et l'écran une autre. */
  const view = useMemo(() => (form ? preview(form, edits) : null), [form, edits]);
  const fields = view?.fields ?? [];

  /**
   * Les questions encore disponibles. Une question déjà posée n'est pas reproposée :
   * deux poses de la même question écriraient sous la même clé, la seconde effaçant la
   * première — un formulaire qui pose deux fois la même question et n'en garde qu'une
   * réponse.
   */
  const available = useMemo(() => {
    if (!form) return [];
    const placed = new Set(
      project(form, edits).flatMap((m) =>
        typeof m.modelId === "number" ? [m.modelId] : [],
      ),
    );
    return questionsFor(form.type, questions ?? []).filter(
      (q) => !placed.has(q.id),
    );
  }, [form, edits, questions]);

  /* La création est la SEULE écriture qui ne passe pas par le brouillon : une question
     doit exister avant d'être posée. D'où son propre état, et sa confirmation. */
  /** Les champs dont la zone « N'afficher que si » est dépliée — voir `useUnfolded`. */
  const unfolded = useUnfolded(duCache?.id);
  const setUnfolded = (next: (was: ReadonlySet<number>) => Set<number>) =>
    duCache && writeUnfolded(duCache.id, next(unfolded));
  const [creation, setCreation] = useState(false);
  const [creant, setCreant] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const key = ["forms", event?.domainId, event?.eventId];

  const putInCache = (f: Form) =>
    client.setQueryData<Form[]>(key, (previous) =>
      (previous ?? []).map((x) => (x.id === f.id ? f : x)),
    );

  /**
   * L'APERÇU, POUSSÉ VERS L'ÉCRAN. `Informations` lit la même clé de cache : y écrire
   * le formulaire retouché le redessine immédiatement, sans rien enregistrer.
   *
   * ⚠️ AUCUN NETTOYAGE AU DÉMONTAGE, ET C'EST DÉLIBÉRÉ. La version d'avant remettait la
   * vérité du serveur en partant : fermer la carte du `SpotEditor` faisait donc
   * disparaître de l'écran des retouches qu'on venait de faire. L'aperçu suit le
   * brouillon — il s'efface quand celui-ci s'efface, à l'enregistrement ou à
   * l'abandon, et pas avant. Le participant, lui, n'a pas ce cache : il ne voit que ce
   * qui est en base.
   */
  useEffect(() => {
    if (draft && view) putInCache(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, view]);

  /**
   * ⚠️ TANT QUE LA MODALE EST OUVERTE, L'ENREGISTREMENT AUTOMATIQUE ATTEND. La création
   * dure deux allers-retours, puis POSE le champ avec le brouillon que ce rendu-ci
   * connaît : un envoi qui aboutit entre les deux oublie le brouillon, la pose le
   * ressuscite périmé, et l'envoi suivant échoue en accusant un collègue — l'erreur
   * relevée en ajoutant une donnée à un événement, en mode automatique. Voir
   * `holdWhileComposing`.
   *
   * La relâche est le nettoyage de l'effet : créée, annulée, ou l'éditeur démonté sous
   * les doigts, le verrou tombe de lui-même.
   */
  useEffect(() => {
    if (!creation) return;
    return holdWhileComposing();
  }, [creation]);

  if (!event) return null;

  /**
   * ⚠️ RIEN À RANGER N'EST PAS RIEN À DIRE. Sans formulaire désigné, la ligne
   * « Présentation des champs » s'affichait NUE — un libellé, et le vide en dessous :
   * l'organisateur n'y lisait pas « choisissez d'abord », il y lisait « cassé ». Le cas
   * n'est pas de bord côté accompagnant, c'est le réglage par défaut (vide = le
   * formulaire de l'événement).
   *
   * ⚠️ LE SILENCE RESTE POUR L'AUTRE CAS. Un formulaire DÉSIGNÉ mais pas encore dans le
   * cache est une lecture en vol, pas une absence : y écrire la même phrase la ferait
   * clignoter à chaque ouverture du panneau.
   */
  if (!form)
    return chosen === undefined ? (
      <p className="row__h">
        Aucun formulaire choisi : rien à présenter. Choisissez-en un ci-dessus.
      </p>
    ) : null;

  /** Ouvre le brouillon s'il n'existe pas encore, et lui applique un changement. */
  const updateDraft = (maj: (b: Draft) => Draft) =>
    writeDraft(
      form.id,
      maj(draft ?? freshDraft(form, event.domainId, event.eventId)),
    );

  /** L'état affiché : la retouche en cours si elle existe, sinon ce qu'a dit le serveur. */
  const vu = <K extends keyof Edit>(
    f: Field,
    key: K,
    server: Edit[K],
  ): Edit[K] => {
    const p = edits.patches.get(Number(f.id));
    return p && p[key] !== undefined ? p[key] : server;
  };

  /** Ce que le champ afficherait sans libellé propre — le filigrane de la saisie. */
  const shared = (f: Field) => f.labelQuestion || f.key || "sans libellé";

  /**
   * Les champs dont celui-ci peut dépendre : ceux DU MÊME formulaire qui proposent des
   * options — un texte libre n'a pas de « réponse donnée » à laquelle s'accrocher —,
   * lui-même exclu. Par question (`modelId`), c'est ainsi que le back office l'écrit.
   */
  const parentsOf = (f: Field) =>
    fields.filter(
      (p) =>
        p.id !== f.id &&
        p.modelId !== null &&
        p.options.length > 0 &&
        ["select", "select-multiple", "radio", "checkbox"].includes(
          (vu(p, "component", p.component) ?? p.component) as string,
        ),
    );

  const editField = (f: Field, patch: Edit) =>
    updateDraft((b) => {
      const id = Number(f.id);
      const patches = new Map(b.patches);
      patches.set(id, { ...patches.get(id), ...patch });
      return { ...b, patches };
    });

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= edits.order.length) return;
    updateDraft((b) => {
      const order = [...b.order];
      const ici = order[index]!;
      order[index] = order[target]!;
      order[target] = ici;
      return { ...b, order };
    });
  };

  const add = (question: Question, type?: string) =>
    updateDraft((b) => ({
      ...b,
      additions: [
        ...b.additions,
        type
          ? { ...newPlacement(question, b.nextId), component: type }
          : newPlacement(question, b.nextId),
      ],
      order: [...b.order, b.nextId],
      /* ⚠️ STRICTEMENT DÉCROISSANT. Ajouter, retirer, puis rajouter aurait sinon
         réattribué le même identifiant à une autre question, et les retouches restées
         sur la première se seraient appliquées à la seconde. */
      nextId: b.nextId - 1,
    }));

  const remove = (id: number) =>
    updateDraft((b) => {
      const patches = new Map(b.patches);
      patches.delete(id);
      return {
        ...b,
        patches,
        order: b.order.filter((r) => r !== id),
        /* ⚠️ UNE POSE JAMAIS ENREGISTRÉE S'OUBLIE, elle ne se « supprime » pas : il n'y
           a rien en base à retirer, et la marquer supprimée aurait demandé au serveur
           d'effacer une ligne qui n'existe pas. */
        additions: id < 0 ? b.additions.filter((m) => m.id !== id) : b.additions,
        removed: id < 0 ? b.removed : new Set(b.removed).add(id),
      };
    });

  const edited = !!draft;

  const discard = () => {
    // L'écran porte encore l'aperçu : on lui rend la vérité avant de jeter le brouillon.
    putInCache(form);
    forgetDraft(form.id);
  };

  return (
    <div className="fed">
      {fields.map((f, i) => {
        const decor = DECORATIVE.has(f.component);
        const name = f.label.trim() || (decor ? "Décor" : f.key);
        const fichier =
          String(vu(f, "component", f.component) ?? f.component) === "file";
        return (
          <div className="fed__r" key={f.id}>
            <div className="fed__t">
              {/**
               * ⚠️ CE QU'ON ÉCRIT ICI EST LE LIBELLÉ DE **CE** FORMULAIRE, pas celui de
               * la question. La question est partagée par tout le domaine : la
               * renommer renommerait le champ dans les formulaires des voisins. Le
               * placement, lui, porte son propre libellé — c'est la colonne prévue pour
               * ça, et c'est celle que la V1 lit en premier.
               *
               * Vidé, il retombe sur le libellé de la question, que le `placeholder`
               * montre : on voit donc toujours ce qu'on obtiendrait en effaçant.
               */}
              <input
                className="fed__n"
                type="text"
                value={vu(f, "label", f.ownLabel) ?? ""}
                placeholder={shared(f)}
                aria-label={`${name} — libellé dans ce formulaire`}
                title={`Libellé dans ce formulaire. Vide, il reprend « ${shared(f)} ». Clé : ${f.key || "aucune"}`}
                onChange={(e) => editField(f, { label: e.target.value })}
              />

              {/* ⚠️ DEUX FLÈCHES ET NON UN GLISSER-DÉPOSER. Le tri à la souris demande
                  une bibliothèque, et surtout il s'atteint mal au clavier — or c'est un
                  panneau où l'on tabule beaucoup. Deux boutons font le même travail,
                  sans dépendance, et se décrivent à un lecteur d'écran. */}
              <span className="fed__o">
                <button
                  type="button"
                  aria-label={`${name} — monter`}
                  title="Monter"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`${name} — descendre`}
                  title="Descendre"
                  disabled={i === fields.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
                {/* ⚠️ RETIRER LE CHAMP, PAS LA QUESTION. Elle reste au domaine,
                    disponible pour les autres formulaires, et les réponses déjà
                    données restent en base — elles sont rangées sous sa clé, que
                    personne ne touche. D'où « retirer » et non « supprimer ». */}
                <button
                  type="button"
                  className="fed__del"
                  aria-label={`${name} — retirer du formulaire`}
                  title="Retirer de ce formulaire"
                  onClick={() => remove(Number(f.id))}
                >
                  ✕
                </button>
              </span>
            </div>

            <div className="fed__b">
              {/* Les deux listes sur leur propre niveau : chacune y dispose de la
                  moitié de la largeur, au lieu du cinquième que cinq contrôles
                  côte à côte lui laissaient. Un champ « Pièce jointe » en a QUATRE :
                  sur une ligne chacune tombait sous les cent pixels (« Pièce joi… »,
                  « 1 » pour « 1 Mo max ») — d'où deux lignes (type + largeur, puis
                  fichiers acceptés + taille), l'ordre du DOM suit. */}
              <div className={"fed__sel" + (fichier ? " fed__sel--file" : "")}>
              {decor ? (
                /* Un commentaire ou un bloc HTML n'a ni type à choisir, ni obligation,
                   ni verrou — mais il a bien une largeur. Une cellule muette plutôt que
                   des contrôles inertes : « sans objet » et « désactivé » ne disent pas
                   la même chose, et un bouton qui ne fait rien se clique quand même. */
                <span className="fed__x">Commentaire</span>
              ) : (
                <select
                  className="fed__s"
                  aria-label={`${name} — type de champ`}
                  value={vu(f, "component", f.component)}
                  onChange={(e) => editField(f, { component: e.target.value })}
                >
                  {typeDe(
                    (vu(f, "component", f.component) ??
                      f.component) as Field["component"],
                    f.options.length > 0,
                  ).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              )}

              <select
                className="fed__s"
                aria-label={`${name} — largeur`}
                value={vu(f, "width", f.width)}
                onChange={(e) => editField(f, { width: e.target.value as Width })}
              >
                {WIDTHS.map((w) => (
                  <option key={w} value={w}>
                    {WIDTH_NAMES[w]}
                  </option>
                ))}
              </select>

              {fichier && (
                <>
                  <select
                    className="fed__s"
                    aria-label={`${name} — fichiers acceptés`}
                    title="Fichiers acceptés"
                    value={vu(f, "accept", f.accept) ?? ""}
                    onChange={(e) => editField(f, { accept: e.target.value })}
                  >
                    {acceptsOf(vu(f, "accept", f.accept) ?? "").map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="fed__s"
                    aria-label={`${name} — taille maximale`}
                    title="Taille maximale"
                    value={String(vu(f, "maxMb", f.maxMb) ?? f.maxMb)}
                    onChange={(e) =>
                      editField(f, { maxMb: Number(e.target.value) })
                    }
                  >
                    {[
                      ...MAX_MBS,
                      ...(MAX_MBS.includes(f.maxMb) ? [] : [f.maxMb]),
                    ].map((m) => (
                      <option key={m} value={String(m)}>
                        {m} Mo max
                      </option>
                    ))}
                  </select>
                </>
              )}
              </div>

              {!decor && (
                <div className="fed__tog">
                  <button
                    type="button"
                    className="fed__c"
                    aria-pressed={!!vu(f, "required", f.required)}
                    aria-label={`${name} — obligatoire`}
                    title="Réponse obligatoire"
                    onClick={() =>
                      editField(f, { required: !vu(f, "required", f.required) })
                    }
                  >
                    Oblig.
                  </button>
                  <button
                    type="button"
                    className="fed__c"
                    aria-pressed={!!vu(f, "locked", f.locked)}
                    aria-label={`${name} — non modifiable une fois rempli`}
                    title="Non modifiable une fois rempli"
                    onClick={() =>
                      editField(f, { locked: !vu(f, "locked", f.locked) })
                    }
                  >
                    Figé
                  </button>
                  {/* Seulement là où il y a des choix à ranger : une liste déroulante
                      et un champ texte n'ont rien à mettre en colonne. */}
                  {WITH_BOXES.has(
                    String(vu(f, "component", f.component) ?? f.component),
                  ) && (
                    <button
                      type="button"
                      className="fed__c"
                      aria-pressed={!!vu(f, "column", f.choicesInColumn)}
                      aria-label={`${name} — choix l'un sous l'autre`}
                      title="Choix l'un sous l'autre, plutôt que côte à côte"
                      onClick={() =>
                        editField(f, {
                          column: !vu(f, "column", f.choicesInColumn),
                        })
                      }
                    >
                      Colonne
                    </button>
                  )}
                  {/* ═══ N'AFFICHER QUE SI… ═══
                      Le réglage du back office (« Condition » / « Afficher si »),
                      au même rang que « Oblig. » et « Figé » : c'est une propriété
                      du champ, pas un troisième type. Allumée quand une condition
                      est posée ; la zone en dessous se déplie au clic et se replie
                      une fois réglée, pour ne pas alourdir chaque ligne. */}
                  {parentsOf(f).length > 0 && (() => {
                    const cond = vu(f, "condition", f.condition) ?? null;
                    const parent = cond
                      ? parentsOf(f).find((p) => p.modelId === cond.modelId)
                      : undefined;
                    const opened = unfolded.has(stableKey(f));
                    return (
                      <button
                        type="button"
                        className="fed__c"
                        aria-pressed={!!cond}
                        aria-expanded={opened}
                        aria-label={`${name} — condition d'affichage`}
                        title={
                          cond && parent
                            ? `Affiché si « ${parent.label || parent.key} » vaut ${cond.keys.join(", ") || "(à choisir)"}`
                            : "N'afficher ce champ que si un autre a reçu une réponse donnée"
                        }
                        onClick={() =>
                          setUnfolded((was) => {
                            const next = new Set(was);
                            if (next.has(stableKey(f))) next.delete(stableKey(f));
                            else next.add(stableKey(f));
                            return next;
                          })
                        }
                      >
                        Condition
                      </button>
                    );
                  })()}
                </div>
              )}

              {!decor && unfolded.has(stableKey(f)) && parentsOf(f).length > 0 && (() => {
                const cond = vu(f, "condition", f.condition) ?? null;
                const parent = cond
                  ? parentsOf(f).find((p) => p.modelId === cond.modelId)
                  : undefined;
                return (
                  <div className="fed__if">
                    <label className="fed__ifl">
                      N'afficher que si
                      <select
                        className="fed__s"
                        value={cond ? String(cond.modelId) : ""}
                        onChange={(e) => {
                          const modelId = Number(e.target.value);
                          editField(f, {
                            condition: modelId ? { modelId, keys: [] } : null,
                          });
                        }}
                      >
                        <option value="">toujours affiché</option>
                        {parentsOf(f).map((p) => (
                          <option key={p.id} value={String(p.modelId)}>
                            « {p.label || p.key} »
                          </option>
                        ))}
                      </select>
                    </label>
                    {parent && (
                      <div className="fed__ifv">
                        <span className="fed__ifl">vaut</span>
                        <div className="fed__tog" role="group" aria-label={`${name} — réponses qui l'affichent`}>
                          {parent.options.map((o) => {
                            const on = cond!.keys.includes(o.value);
                            return (
                              <button
                                key={o.value}
                                type="button"
                                className="fed__c"
                                aria-pressed={on}
                                onClick={() =>
                                  editField(f, {
                                    condition: {
                                      modelId: cond!.modelId,
                                      keys: on
                                        ? cond!.keys.filter((k) => k !== o.value)
                                        : [...cond!.keys, o.value],
                                    },
                                  })
                                }
                              >
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                        {cond!.keys.length === 0 && (
                          <span className="fed__x">Sans réponse cochée, le champ reste affiché.</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })}

      {/**
       * AJOUTER UN CHAMP — c'est-à-dire POSER une question qui existe déjà.
       *
       * ⚠️ ON N'EN CRÉE PAS. La liste est celle du domaine, filtrée comme le fait le
       * back office : un formulaire de participant accueille les questions `U` et
       * `UE`, un formulaire de société uniquement des `C`. Créer une question toucherait
       * tous les formulaires du domaine — c'est la frontière que ce dépôt défend, et
       * elle reste au back office.
       *
       * Un `<select>` qui se remet à zéro plutôt qu'un menu : il ne porte pas de
       * valeur, il déclenche une action. Le `value=""` permanent le dit.
       */}
      {/* ⚠️ TOUJOURS AFFICHÉE, MÊME SANS QUESTION DISPONIBLE. La version d'avant ne la
          montrait que s'il restait quelque chose à poser : un formulaire qui les avait
          toutes n'offrait plus aucun moyen d'en créer une. */}
      <select
        className="fed__add"
        value=""
        aria-label="Ajouter un champ au formulaire"
        onChange={(e) => {
          if (e.target.value === CREATE) return setCreation(true);
          const q = available.find((x) => String(x.id) === e.target.value);
          if (q) add(q);
        }}
      >
        <option value="">+ Ajouter un champ</option>
        {available.length > 0 && (
          <optgroup label="Questions existantes">
            {available.map((q) => (
              <option key={q.id} value={q.id}>
                {q.label}
                {q.model === "UE" ? " (cet événement)" : ""}
              </option>
            ))}
          </optgroup>
        )}
        {/**
         * ⚠️ PAS SUR UN FORMULAIRE DE SOCIÉTÉ, et c'est un piège évité plutôt qu'une
         * fonction retirée. `creerQuestion` crée une donnée ÉVÉNEMENTIELLE (`UE`),
         * seule portée sans conséquence pour les voisins — voir sa note. Or un
         * formulaire de société n'accueille que des questions `C` (`questionsFor`,
         * la règle du back office) : la donnée serait bel et bien créée, puis
         * ÉCARTÉE du choix. Créer quelque chose qui disparaît est pire que ne pas
         * l'offrir. Les données de société se créent au back office.
         */}
        {form?.type !== "C" && (
          <optgroup label="Nouvelle">
            <option value={CREATE}>＋ Créer une donnée</option>
          </optgroup>
        )}
      </select>

      <NewQuestion
        open={creation}
        busy={creant}
        error={createError}
        onCancel={() => {
          setCreation(false);
          setCreateError(null);
        }}
        onCreate={async (name, type, options) => {
          setCreant(true);
          setCreateError(null);
          try {
            const created = await createQuestion(
              event.domainId,
              event.eventId,
              name,
              options.map((label) => ({ label })),
              questions ?? [],
            );
            /* La liste des questions a changé côté serveur : sans invalidation, la
               nouvelle resterait absente du choix jusqu'au rechargement. */
            await client.invalidateQueries({
              queryKey: ["questions", event.domainId, event.eventId],
            });
            // Créée ET posée : personne ne crée une donnée pour ne pas s'en servir.
            add(created, type);
            setCreation(false);
          } catch (cause) {
            setCreateError(
              cause instanceof Error && cause.message
                ? cause.message
                : "La donnée n'a pas pu être créée.",
            );
          } finally {
            setCreant(false);
          }
        }}
      />

      {/**
       * ⚠️ PLUS DE BOUTON « ENREGISTRER » ICI, ET C'EST UNE CORRECTION. Il en existait
       * un, propre au formulaire, qui envoyait quel que soit le mode choisi. Il
       * contredisait le réglage : l'organisateur ayant demandé l'automatique devait
       * quand même cliquer, et celui ayant demandé le manuel se retrouvait avec DEUX
       * boutons — un pour ses réglages, un pour son formulaire — sans que rien ne dise
       * pourquoi.
       *
       * Le mode d'enregistrement appartient à l'ORGANISATEUR, pas à un écran. Le
       * formulaire suit donc les réglages : automatique, il part seul sur la même
       * cadence ; manuel, il part avec eux, sur le même bouton. Voir
       * `formDraft.enregistrerBrouillons` et `OrganiserBar`.
       *
       * Reste « Abandonner », qui n'a pas d'équivalent côté réglages : le brouillon
       * survit désormais au démontage, il faut donc pouvoir dire qu'on n'en veut pas.
       * Sans lui, un aperçu délaissé resterait à l'écran jusqu'au rechargement.
       */}
      {edited && (
        <div className="fed__a">
          <span className="fed__att">
            {saveMode === "auto"
              ? "En attente, part dans quelques secondes."
              : "En attente, partira avec vos réglages."}
          </span>
          <Button
            type="button"
            variant="ghost"
            className="btn--off"
            onClick={discard}
          >
            Abandonner
          </Button>
        </div>
      )}

      {draft?.failure && (
        <p className="fed__err" role="alert">
          {draft.failure}
        </p>
      )}
    </div>
  );
}
